import { Link } from "react-router-dom";
import { usePublishedExperiences } from "@/hooks/useExperiences";
import { Skeleton } from "@/components/ui/skeleton";
import { track } from "@/lib/analytics";
import { useTranslate } from "@/i18n";

/**
 * Landing v2 — Direction section
 * "Vart är du på väg?" — feels like choosing a path, not filtering.
 * Cards are entirely clickable with subtle hover elevation.
 */

const LABEL_MAP: Record<string, string> = {
  "nara-naturen": "Nära naturen",
  "i-rorelse": "I rörelse",
  "smak-av-garden": "Smak av gården",
  "stilla-platser": "Stilla platser",
  "det-hander-har": "Det händer här",
};

/** Short, rhythmically balanced ingress overrides for the landing cards. */
const INTRO_MAP: Record<string, string> = {
  "nara-naturen": "Skog, vatten och öppna landskap – platser att bara vara i.",
  "i-rorelse": "Vandring, paddling och stigar genom landskapet.",
  "smak-av-garden": "Gårdsbutiker, mathantverk och bord som samlar.",
};

function getLabel(slug: string, title: string): string {
  return LABEL_MAP[slug] ?? title;
}

function getIntro(slug: string, intro: string | null): string | null {
  return INTRO_MAP[slug] ?? intro;
}

export default function DirectionSection() {
  const { data: experiences, isLoading } = usePublishedExperiences();
  const t = useTranslate();

  if (!isLoading && (!experiences || experiences.length === 0)) return null;

  return (
    <section id="direction" className="bg-farmly-soft">
      <div className="container max-w-3xl px-5 pt-14 pb-16 sm:pt-16 sm:pb-20">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground tracking-[-0.015em] leading-[1.15]">
          {t("home.direction.title")}
        </h2>
        <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg">
          {t("home.direction.sub")}
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-foreground/[0.06] bg-background p-7">
                  <Skeleton className="h-5 w-3/5 mb-3" />
                  <Skeleton className="h-4 w-4/5" />
                </div>
              ))
            : experiences!.map((exp, idx) => (
                <Link
                  key={exp.slug}
                  to={`/upplevelse/${exp.slug}`}
                  onClick={() =>
                    track("experience_opened", {
                      experience_slug: exp.slug,
                      surface: "lens",
                      href: `/upplevelse/${exp.slug}`,
                      position: idx,
                    }, "router")
                  }
                  className="group flex flex-col justify-between rounded-2xl border border-foreground/[0.06] bg-background p-7 sm:p-8 cursor-pointer transition-[transform,box-shadow,border-color] duration-[180ms] ease-out hover:shadow-[0_10px_20px_-6px_hsl(var(--foreground)/0.08)] hover:-translate-y-[3px] hover:border-foreground/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground group-hover:text-accent transition-colors duration-[160ms] leading-[1.2]">
                      {getLabel(exp.slug, exp.title)}
                    </h3>
                    {(() => {
                      const intro = getIntro(exp.slug, exp.intro);
                      return intro ? (
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2 sm:line-clamp-3">
                          {intro}
                        </p>
                      ) : null;
                    })()}
                  </div>
                  <span className="mt-5 inline-flex items-center gap-1 text-xs text-muted-foreground/50 group-hover:text-accent/70 transition-colors duration-[160ms]">
                    {t("cta.explore")} <span className="inline-block transition-transform duration-[180ms] ease-out group-hover:translate-x-1">→</span>
                  </span>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
}
